import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Info } from 'lucide-react'
import { apiGet } from '../api'
import { useFacility } from '../context/FacilityContext'
import { Loading, PageHeader, StatusPill, tournamentStatus } from '../components/ui'
import './pages.css'

const FORMAT_LABELS = {
  ROUND_ROBIN: 'Seriespill',
  KNOCKOUT: 'Cup',
  GROUPS_THEN_KNOCKOUT: 'Grupper + cup'
}

// PADEL → Padel. Andre verdier vises som de er.
const prettySport = (s) => (s && s === s.toUpperCase() ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : s)

const fmtDay = (d, withYear) =>
  d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) }).replace('.', '')

function fmtRange(start, end) {
  const a = start ? new Date(start) : null
  const b = end ? new Date(end) : null
  if (!a || Number.isNaN(a.getTime())) return ''
  if (!b || Number.isNaN(b.getTime()) || a.toDateString() === b.toDateString()) return fmtDay(a, true)
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${a.getDate()}.–${fmtDay(b, true)}`
  }
  return `${fmtDay(a, a.getFullYear() !== b.getFullYear())} – ${fmtDay(b, true)}`
}

function TournamentsPage() {
  const { activeFacilityId } = useFacility()
  const [tournaments, setTournaments] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!activeFacilityId) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFacilityId])

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiGet(`/api/tournaments/facility/${activeFacilityId}`)
      if (res.ok) setTournaments(await res.json())
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Turneringer"
        subtitle="Administrer turneringer anlegget arrangerer — oppsett, kamper og resultater."
      />

      {loading ? (
        <Loading />
      ) : tournaments.length === 0 ? (
        <div className="empty-row">
          <span className="empty-row-icon"><Info size={22} strokeWidth={1.9} aria-hidden="true" /></span>
          <div className="empty-row-text">
            <span className="empty-row-title">Ingen turneringer ennå</span>
            <span className="empty-row-desc">Opprett en turnering i Spelaren-appen og velg anlegget som arrangør.</span>
          </div>
        </div>
      ) : (
        <div className="tour-grid">
          {tournaments.map((t) => {
            const st = tournamentStatus(t.status)
            const dates = fmtRange(t.startsAt, t.endsAt)
            return (
              <article key={t.id} className="tour-card">
                <div className="tour-card-top">
                  <span className={`tour-mono${st.tone === 'live' ? ' live' : ''}`} aria-hidden="true">
                    {(prettySport(t.sportType) || t.name || '?').slice(0, 2)}
                  </span>
                  <StatusPill tone={st.tone} large>{st.label}</StatusPill>
                </div>
                <div className="tour-card-head">
                  <h2 className="tour-card-title">{t.name}</h2>
                  {dates && <span className="tour-card-dates">{dates}</span>}
                </div>
                <div className="tour-chips">
                  {t.sportType && <span className="chip">{prettySport(t.sportType)}</span>}
                  {t.format && <span className="chip">{FORMAT_LABELS[t.format] || t.format}</span>}
                  <span className="chip">{t.teamCount ?? 0} lag</span>
                </div>
                <Link className="btn btn-secondary tour-open" to={`/tournaments/${t.id}`} aria-label={`Åpne dashboard for ${t.name}`}>
                  Åpne dashboard
                  <ArrowRight size={18} strokeWidth={1.9} aria-hidden="true" />
                </Link>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TournamentsPage
